import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { Select, FormPageHeader, FileInput, Textarea } from 'components/Shared';
import { FORM_BACK_ACTION, SELECT_OPTIONS } from 'constants/Common';
import { useValidation } from 'hooks';
import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from 'store';
import { CreateDocumentActions as CreateActions } from 'store/ducks/documents';
import { CreateDocumentValidator } from 'validators/Documents';
import * as S from './styles';
import { formDataBuilder } from 'services';
import { CreateDocument } from 'contracts/Documents';

interface Props {
  onCreate?: () => void;
}

export const DocumentCreationForm: React.FC<Props> = ({ onCreate }) => {
  const dispatch: AppDispatch = useDispatch();
  const formRef = useRef<FormHandles>(null);
  const { handleFormErrors, handleApiErrors } = useValidation();

  const { loading: creatingDocument, validationErrors } = useSelector(
    (state: RootState) => state.createDocument
  );

  const onSuccess = useCallback((): void => {
    formRef?.current?.reset();
    onCreate && onCreate();
  }, [onCreate]);

  const onSubmit = useCallback(
    async (data: CreateDocument): Promise<void> => {
      try {
        formRef?.current?.setErrors({});

        const { schema } = new CreateDocumentValidator();

        await schema.validate(data, {
          abortEarly: false,
        });

        const payload = formDataBuilder({
          ...data,
          fileName: data.file.name
        })

        dispatch(CreateActions.request(payload, onSuccess));
      } catch (error) {
        handleFormErrors(error, formRef);
      }
    },
    [dispatch, handleFormErrors, onSuccess]
  );

  const Header = useCallback((): JSX.Element => {
    return (
      <FormPageHeader
        title="Novo documento"
        icon={<S.BookMarkIcon />}
        actions={
          <S.LinkButton size="small" to="/documentacoes">
            <S.ArrowLeftIcon /> Voltar
          </S.LinkButton>
        }
      />
    );
  }, []);

  useEffect(() => {
    handleApiErrors(validationErrors, formRef);
  }, [handleApiErrors, validationErrors]);

  useEffect(() => {
    return () => {
      dispatch(CreateActions.reset());
    };
  }, [dispatch]);

  return (
    <S.MainPanel>
      <Header />
      <Form ref={formRef} onSubmit={onSubmit}>
        <S.FormRow>
          <FileInput
            name="file"
            label={'Arquivo'}
            accept=".png, .jpeg, .jpg, .pdf, .xlsx, .xls"
          />
          <Select
            name="category"
            label="Categoria"
            options={SELECT_OPTIONS.CATEGORY}
            defaultValue={SELECT_OPTIONS.CATEGORY[0]}
          />
        </S.FormRow>
        <S.FormRow>
          <Textarea
            name="description"
            label={'Descrição'}
            style={{ resize: 'none' }}
          />
        </S.FormRow>

        <S.FormActions>
          <S.LinkButton mood="light" to="/documentacoes">
            {FORM_BACK_ACTION}
          </S.LinkButton>
          <S.Button type="submit">
            {creatingDocument ? <S.ActivityIndicator /> : 'Salvar'}
          </S.Button>
        </S.FormActions>
      </Form>
    </S.MainPanel>
  );
};
